import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Plus, Users, Wrench, Building2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { MachineCombobox } from "@/components/MachineCombobox";
import { CustomerDialog } from "@/components/CustomerDialog";
import { MachineDialog } from "@/components/MachineDialog";
import { StatusPill } from "@/components/StatusPill";
import { TypeBadge } from "@/components/Badges";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

export const EMPTY_MACHINE_INFO = { brand: "", type: "", internal_number: "", serial_number: "" };
export const hasMachineInfo = (mi) => !!mi && Object.values(mi).some((v) => (v || "").trim());

/** Searchable customer picker (name, contact, department, org.nr). */
export const CustomerCombobox = ({ customers = [], value, onChange, placeholder = "Vel kunde", testid = "customer-combobox", disabled }) => {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => customers.find((c) => c.id === value), [customers, value]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled} data-testid={testid}
          className={cn("h-12 w-full justify-between px-3 font-normal", !selected && "text-muted-foreground")}>
          <span className="flex min-w-0 items-center gap-2">
            <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">
              {selected ? (
                <>
                  <span className="font-semibold text-foreground">{selected.name}</span>
                  {selected.department && <span className="text-muted-foreground"> · {selected.department}</span>}
                </>
              ) : placeholder}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command filter={(val, search) => (val.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder="Søk kunde, kontakt, org.nr …" data-testid={`${testid}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty>Ingen kundar funne.</CommandEmpty>
            <CommandGroup>
              {customers.map((c) => (
                <CommandItem key={c.id} value={[c.name, c.department, c.contact_person, c.org_number, c.phone].filter(Boolean).join(" ")}
                  onSelect={() => { onChange(c.id); setOpen(false); }} data-testid={`${testid}-option-${c.id}`} className="cursor-pointer gap-3 py-2.5">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
                    {c.entity_type === "privat" ? <Users className="h-3.5 w-3.5" /> : <Building2 className="h-3.5 w-3.5" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{c.name}</div>
                    <div className="truncate text-xs text-muted-foreground">{[c.contact_person, c.address].filter(Boolean).join(" · ") || (c.customer_type === "serviceavtale" ? "Serviceavtale" : "Engangskunde")}</div>
                  </div>
                  <Check className={cn("h-4 w-4", value === c.id ? "opacity-100" : "opacity-0")} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

/** Free-text machine details for reports on unregistered equipment (engangskunde). */
export const MachineInfoFields = ({ value, onChange, testid = "machine-info" }) => {
  const v = { ...EMPTY_MACHINE_INFO, ...(value || {}) };
  const set = (k) => (e) => onChange({ ...v, [k]: e.target.value });
  return (
    <div className="space-y-2 rounded-xl border border-dashed p-3" data-testid={`${testid}-fields`}>
      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>Maskin/utstyr som <span className="font-medium text-foreground">ikkje</span> er registrert i systemet. Fyll inn det du veit – blir med i PDF-en. Ingen fristar blir oppdaterte.</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Merke</Label>
          <Input className="h-11" value={v.brand} onChange={set("brand")} placeholder="Volvo" data-testid={`${testid}-brand`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Modell</Label>
          <Input className="h-11" value={v.type} onChange={set("type")} placeholder="EC220E" data-testid={`${testid}-type`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Internnr <span className="font-normal text-muted-foreground">– kundens</span></Label>
          <Input className="h-11" value={v.internal_number} onChange={set("internal_number")} placeholder="t.d. 204" data-testid={`${testid}-internal`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Serienr <span className="font-normal text-muted-foreground">– valfritt</span></Label>
          <Input className="h-11" value={v.serial_number} onChange={set("serial_number")} placeholder="SN" data-testid={`${testid}-serial`} />
        </div>
      </div>
    </div>
  );
};

/**
 * ReportSubjectPicker – «Kva gjeld rapporten?» for både service- og jobbrapport.
 *   mode="machine"  : registrert maskin (maskinveljar + hurtigoppretting)
 *   mode="customer" : berre kunde (kundeveljar + hurtigoppretting) + valfri fritekst maskininfo
 */
export const ReportSubjectPicker = ({
  machines = [], customers = [],
  mode, onModeChange,
  machineId, onMachineChange,
  customerId, onCustomerChange,
  machineInfo, onMachineInfoChange,
  onMachinesChanged, onCustomersChanged,
  testid = "report-subject",
}) => {
  const [newCustomer, setNewCustomer] = useState(false);
  const [newMachine, setNewMachine] = useState(false);
  const selectedMachine = useMemo(() => machines.find((m) => m.id === machineId), [machines, machineId]);
  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === (mode === "machine" ? selectedMachine?.customer_id : customerId)),
    [customers, mode, selectedMachine, customerId]
  );

  const ModeBtn = ({ value, icon: Icon, label, sub, tid }) => (
    <button type="button" onClick={() => onModeChange(value)} data-testid={tid} aria-pressed={mode === value}
      className={cn("pressable flex min-h-[52px] flex-1 items-center gap-3 rounded-xl border px-3 text-left transition-colors duration-150",
        mode === value ? "border-transparent bg-[#0B0D10] text-white dark:bg-white dark:text-[#0B0D10]" : "bg-card hover:bg-accent")}>
      <Icon className="h-5 w-5 shrink-0" />
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{label}</span>
        <span className={cn("block truncate text-[11px]", mode === value ? "opacity-70" : "text-muted-foreground")}>{sub}</span>
      </span>
    </button>
  );

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex gap-2" data-testid={`${testid}-mode`}>
        <ModeBtn value="machine" icon={Wrench} label="Registrert maskin" sub="Oppdaterer fristar og historikk" tid={`${testid}-mode-machine`} />
        <ModeBtn value="customer" icon={Users} label="Berre kunde" sub="Engangskunde / utstyr utan register" tid={`${testid}-mode-customer`} />
      </div>

      {mode === "machine" ? (
        <div className="space-y-1.5">
          <Label className="text-sm font-semibold">Maskin *</Label>
          <div className="flex gap-2">
            <div className="min-w-0 flex-1"><MachineCombobox machines={machines} value={machineId} onChange={onMachineChange} testid={`${testid}-machine`} /></div>
            <Button type="button" variant="outline" className="h-12 shrink-0 px-3" onClick={() => setNewMachine(true)} aria-label="Ny maskin" data-testid={`${testid}-new-machine`}>
              <Plus /> <span className="hidden sm:inline">Ny maskin</span>
            </Button>
          </div>
          {selectedMachine && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-muted/60 px-3 py-2.5 text-sm" data-testid="selected-machine-info">
              <span className="font-semibold">{selectedMachine.customer_name || "—"}</span>
              {selectedMachine.serial_number && <span className="text-muted-foreground">SN {selectedMachine.serial_number}</span>}
              {selectedMachine.internal_number && <span className="text-muted-foreground">Maskinnr {selectedMachine.internal_number}</span>}
              {selectedMachine.status?.next_service_date && <span className="text-muted-foreground">Neste service {formatDate(selectedMachine.status.next_service_date)}</span>}
              <StatusPill status={selectedMachine.status?.overall_status} className="ml-auto" />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Kunde *</Label>
            <div className="flex gap-2">
              <div className="min-w-0 flex-1"><CustomerCombobox customers={customers} value={customerId} onChange={onCustomerChange} testid={`${testid}-customer`} /></div>
              <Button type="button" variant="outline" className="h-12 shrink-0 px-3" onClick={() => setNewCustomer(true)} aria-label="Ny kunde" data-testid={`${testid}-new-customer`}>
                <Plus /> <span className="hidden sm:inline">Ny kunde</span>
              </Button>
            </div>
            {selectedCustomer && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-muted/60 px-3 py-2.5 text-sm" data-testid="selected-customer-info">
                <span className="font-semibold">{selectedCustomer.name}</span>
                {selectedCustomer.contact_person && <span className="text-muted-foreground">{selectedCustomer.contact_person}</span>}
                {selectedCustomer.phone && <span className="text-muted-foreground">{selectedCustomer.phone}</span>}
                <TypeBadge type={selectedCustomer.customer_type} className="ml-auto" />
              </div>
            )}
          </div>
          <MachineInfoFields value={machineInfo} onChange={onMachineInfoChange} testid={`${testid}-machine-info`} />
        </div>
      )}

      <CustomerDialog
        open={newCustomer}
        onOpenChange={setNewCustomer}
        onSaved={(c) => { onCustomersChanged?.(c); if (c?.id) onCustomerChange(c.id); }}
      />
      <MachineDialog
        open={newMachine}
        onOpenChange={setNewMachine}
        defaultCustomerId={mode === "customer" ? customerId : undefined}
        onSaved={(m) => { onMachinesChanged?.(m); if (m?.id) { onModeChange("machine"); onMachineChange(m.id); } }}
      />
    </div>
  );
};
